import React from 'react';
import { Card } from 'antd';
import { SkBar } from './_primitives';

const BranchesHeader = () => (
  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4 sm:mb-6">
    <div className="flex flex-col gap-2 min-w-0 flex-1">
      <SkBar h="h-7" w="w-56" className="max-w-full" />
      <SkBar h="h-3" w="w-72" className="max-w-full" />
    </div>
    <div className="flex gap-2 flex-shrink-0">
      <SkBar h="h-9" w="w-28" className="rounded-md" />
      <SkBar h="h-9" w="w-28" className="rounded-md" />
    </div>
  </div>
);

const StatCard = () => (
  <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <SkBar h="h-3" w="w-28" />
        <SkBar h="h-7" w="w-12" />
      </div>
      <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
    </div>
  </Card>
);

const RevenueBanner = () => (
  <Card className="shadow-sm mb-4 sm:mb-6" styles={{ body: { padding: 20 } }}>
    <div className="flex items-center justify-between gap-3">
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <SkBar h="h-3" w="w-52" />
        <SkBar h="h-7" w="w-44" />
      </div>
      <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
    </div>
  </Card>
);

const FilterBlock = () => (
  <div className="flex flex-col gap-3 mb-4">
    <SkBar h="h-10" w="w-full" className="rounded-md" />
    <SkBar h="h-10" w="w-full sm:w-48" className="rounded-md" />
  </div>
);

const BRANCH_COLS = ['w-20', 'w-20', 'w-16', 'w-14', 'w-12', 'w-12', 'w-14', 'w-14'];

const BranchRow = () => (
  <div className="grid grid-cols-[2fr_1.2fr_1.8fr_1fr_0.6fr_0.6fr_1fr_0.6fr] gap-3 py-4 border-b border-gray-100 last:border-b-0 items-center">
    {/* Branch: avatar + name + code */}
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <SkBar h="h-3" w="w-40" />
        <SkBar h="h-2" w="w-32" />
      </div>
    </div>
    {/* Location: pin + city + country */}
    <div className="flex items-start gap-1 min-w-0">
      <div className="w-3 h-3 rounded bg-gray-200 animate-pulse flex-shrink-0 mt-1" />
      <div className="flex flex-col gap-1.5 min-w-0">
        <SkBar h="h-3" w="w-16" />
        <SkBar h="h-2" w="w-20" />
      </div>
    </div>
    {/* Contact: phone + email */}
    <div className="flex flex-col gap-1.5 min-w-0">
      <SkBar h="h-3" w="w-24" />
      <SkBar h="h-2" w="w-48" />
    </div>
    {/* Hours */}
    <div className="flex flex-col gap-1">
      <SkBar h="h-3" w="w-16" />
      <SkBar h="h-2" w="w-10" />
      <SkBar h="h-2" w="w-10" />
    </div>
    {/* Floors */}
    <SkBar h="h-3" w="w-4" />
    {/* Zone */}
    <SkBar h="h-3" w="w-10" />
    {/* Status switch */}
    <SkBar h="h-6" w="w-16" className="rounded-full" />
    {/* Actions */}
    <SkBar h="h-6" w="w-6" className="rounded-md justify-self-end" />
  </div>
);

const BranchesPagination = () => (
  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mt-4 pt-3">
    <SkBar h="h-3" w="w-44" />
    <div className="flex items-center gap-3">
      <SkBar h="h-3" w="w-28" />
      <div className="flex items-center gap-2">
        <SkBar h="h-8" w="w-8" className="rounded-md" />
        <SkBar h="h-8" w="w-8" className="rounded-md" />
        <SkBar h="h-8" w="w-8" className="rounded-md" />
      </div>
      <SkBar h="h-8" w="w-24" className="rounded-md" />
    </div>
  </div>
);

/**
 * ProductBranchesPageLoader — matches Product Branches layout:
 *  header (title/subtitle · Map View + Refresh) · 4 stat cards (icon right) ·
 *  Average Monthly Revenue Target banner · card with search + status filter ·
 *  branches table (avatar+name+code · location · contact · hours · floors · zone · status · actions).
 */
const ProductBranchesPageLoader = () => (
  <div className="w-full p-3 sm:p-4 md:p-6">
    <BranchesHeader />

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <StatCard key={i} />
      ))}
    </div>

    <RevenueBanner />

    <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
      <FilterBlock />
      <div className="overflow-x-auto">
        <div className="min-w-[1100px]">
          <div className="grid grid-cols-[2fr_1.2fr_1.8fr_1fr_0.6fr_0.6fr_1fr_0.6fr] gap-3 pb-3 border-b border-gray-200">
            {BRANCH_COLS.map((w, i) => (
              <SkBar
                key={i}
                h="h-3"
                w={w}
                className={i === BRANCH_COLS.length - 1 ? 'justify-self-end' : ''}
              />
            ))}
          </div>
          {Array.from({ length: 6 }).map((_, i) => (
            <BranchRow key={i} />
          ))}
        </div>
      </div>
      <BranchesPagination />
    </Card>
  </div>
);

export default ProductBranchesPageLoader;
